import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, Clock, ExternalLink, Loader2, Rss } from 'lucide-react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { insights } from '@/data/mock';
import { useScrollReveal } from '@/hooks/useScrollReveal';

const Reveal = ({ children, className = '', delay = 0 }) => {
  const [ref, isVisible] = useScrollReveal();
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(24px)',
        transition: `opacity 0.7s cubic-bezier(0.16,1,0.3,1) ${delay}ms, transform 0.7s cubic-bezier(0.16,1,0.3,1) ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CyberFeed = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ref, isVisible] = useScrollReveal();

  useEffect(() => {
    const fetchFeed = async () => {
      try {
        const res = await axios.get(`${API}/cyber-feed`);
        setArticles(res.data.articles || []);
      } catch (err) {
        console.error('Failed to fetch cyber feed');
      } finally {
        setLoading(false);
      }
    };
    fetchFeed();
  }, []);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  return (
    <section className="bg-white py-20 lg:py-28 border-t border-slate-100">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div
          ref={ref}
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(24px)',
            transition: 'opacity 0.7s cubic-bezier(0.16,1,0.3,1), transform 0.7s cubic-bezier(0.16,1,0.3,1)',
          }}
        >
          <div className="flex items-center gap-3 mb-3">
            <Rss className="h-4 w-4 text-[#3B82A0]" />
            <p className="text-[#3B82A0] text-[11px] font-medium uppercase tracking-[0.25em]">
              Live Cyber Intelligence
            </p>
          </div>
          <h2 className="text-2xl md:text-3xl font-light text-[#0B1D33] mb-5 tracking-tight">
            Latest in Cybersecurity
          </h2>
          <p className="text-slate-500 leading-relaxed font-light mb-12 max-w-2xl">
            Real-time cybersecurity news and analysis from leading industry sources.
          </p>

          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-6 w-6 text-[#3B82A0] animate-spin" />
              <span className="ml-3 text-sm text-slate-400">Loading latest articles...</span>
            </div>
          ) : articles.length === 0 ? (
            <p className="text-slate-400 text-sm py-8">Unable to load feed at this time.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {articles.slice(0, 12).map((article, i) => (
                <a
                  key={i}
                  href={article.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group block"
                >
                  <div className="bg-[#F8FAFC] rounded-lg border border-slate-200/80 p-6 h-full hover:border-[#3B82A0]/30 hover:shadow-md transition-shadow transition-colors duration-300">
                    <div className="flex items-center gap-2 mb-3">
                      <Badge
                        variant="secondary"
                        className="text-[9px] font-medium uppercase tracking-wider bg-slate-100 text-slate-500 hover:bg-slate-100"
                      >
                        {article.source}
                      </Badge>
                      {article.published && (
                        <span className="text-[11px] text-slate-400">
                          {formatDate(article.published)}
                        </span>
                      )}
                    </div>
                    <h3 className="text-sm font-medium text-[#0B1D33] mb-2 group-hover:text-[#3B82A0] transition-colors duration-200 leading-snug line-clamp-2">
                      {article.title}
                    </h3>
                    {article.summary && (
                      <p className="text-[12px] text-slate-500 leading-relaxed line-clamp-2 font-light mb-3">
                        {article.summary}
                      </p>
                    )}
                    <span className="text-[11px] text-[#3B82A0] font-medium flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                      Read on {article.source}
                      <ExternalLink className="h-3 w-3" />
                    </span>
                  </div>
                </a>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default function InsightsPage() {
  return (
    <div>
      {/* Header */}
      <section className="bg-[#0B1D33] pt-32 pb-20 lg:pt-36 lg:pb-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <p className="text-[#3B82A0] text-[11px] font-medium uppercase tracking-[0.3em] mb-4">
            Thought Leadership
          </p>
          <h1 className="text-3xl md:text-4xl font-light text-white mb-5 tracking-tight">
            Insights
          </h1>
          <p className="text-slate-300/80 text-lg max-w-2xl leading-relaxed">
            Perspectives on cybersecurity strategy, compliance, governance, and the evolving
            landscape of enterprise security.
          </p>
        </div>
      </section>

      {/* Featured */}
      <section className="bg-[#F8FAFC] py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {/* Featured Article */}
          {insights[0] && (
            <Reveal>
              <Link to={`/insights/${insights[0].id}`} className="block">
                <div className="bg-white rounded-lg border border-slate-200/80 p-8 lg:p-12 mb-8 group hover:shadow-md transition-shadow duration-300">
                  <div className="flex items-center gap-3 mb-5">
                    <Badge
                      variant="secondary"
                      className="text-[10px] font-semibold uppercase tracking-wider bg-[#3B82A0]/10 text-[#3B82A0] hover:bg-[#3B82A0]/10"
                    >
                      {insights[0].category}
                    </Badge>
                    <span className="text-xs text-slate-400">{insights[0].date}</span>
                    <span className="text-xs text-slate-400 flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {insights[0].readTime}
                    </span>
                  </div>
                  <h2 className="text-xl md:text-2xl font-light text-[#0B1D33] mb-4 group-hover:text-[#3B82A0] transition-colors duration-200 tracking-tight">
                    {insights[0].title}
                  </h2>
                  <p className="text-slate-500 leading-relaxed max-w-3xl text-[15px]">
                    {insights[0].excerpt}
                  </p>
                  <div className="mt-6 flex items-center text-[#3B82A0] text-sm font-medium">
                    Read Article
                    <ArrowUpRight className="h-4 w-4 ml-1.5" />
                  </div>
                </div>
              </Link>
            </Reveal>
          )}

          {/* Article Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {insights.slice(1).map((article, i) => (
              <Reveal key={article.id} delay={i * 80}>
                <Link to={`/insights/${article.id}`} className="block h-full">
                  <div className="bg-white rounded-lg border border-slate-200/80 p-7 h-full group hover:shadow-md transition-shadow duration-300">
                    <div className="flex items-center gap-3 mb-4">
                      <Badge
                        variant="secondary"
                        className="text-[10px] font-semibold uppercase tracking-wider bg-slate-100 text-slate-500 hover:bg-slate-100"
                      >
                        {article.category}
                      </Badge>
                      <span className="text-xs text-slate-400">{article.date}</span>
                    </div>
                    <h3 className="text-lg font-semibold text-[#0B1D33] mb-3 group-hover:text-[#3B82A0] transition-colors duration-200 leading-snug">
                      {article.title}
                    </h3>
                    <p className="text-sm text-slate-500 leading-relaxed mb-4">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-slate-400 flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {article.readTime}
                      </span>
                      <span className="text-sm text-[#3B82A0] font-medium flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                        Read
                        <ArrowUpRight className="h-3.5 w-3.5" />
                      </span>
                    </div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Live Cyber News Feed */}
      <CyberFeed />

      {/* Subscribe CTA */}
      <section className="bg-white py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal>
            <div className="bg-[#0B1D33] rounded-xl p-10 lg:p-16 relative overflow-hidden">
              <div className="absolute inset-0 grid-pattern" />
              <div className="relative max-w-2xl mx-auto text-center">
                <h2 className="text-2xl font-light text-white mb-5 tracking-tight">
                  Stay Informed
                </h2>
                <p className="text-slate-400 leading-relaxed mb-8 font-light">
                  Our insights draw on direct advisory experience across the industries and challenges
                  that define enterprise cybersecurity today.
                </p>
                <Link to="/contact">
                  <Button className="bg-[#3B82A0] hover:bg-[#2D6A85] text-white px-8 h-11 text-sm font-medium rounded transition-colors duration-200">
                    Contact Our Team
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
